import {
  FeatureFlag,
  getAllABVariationFlags,
} from "~/common/Featbit/getABFeatureFlag";
import Store from "~/common/Store";
import {
  getExclusiveTestIfEligible,
  updateStorageForExclusiveTest,
} from "./exclusiveTestHelpers";
import {
  getExpiredTestsFromCookie,
  updateStorageForExpiredTests,
} from "./expiredTestHelpers";
import {
  parseActiveFeatBitTests,
  updateStorageForNewTests,
} from "./activeTestHelpers";
import {
  getCurrentVariationIdFromCookie,
  handleChangedVariationIdCookie,
} from "./testTrackingHelpers";

export const ABTestLimit = 6;

export interface DecodedTestInformation {
  exclusive: boolean;
  fullTestId: string;
  variant: string;
  fullVariationId: string;
  kind?: string;
}

let hasRun = false;
// Workaround for test purposes. Could also be used if ever needed to reset and rerun the actual retrieval of FeatBit AB Flags.
export function setHasRun(value: boolean) {
  hasRun = value;
}

export async function getAndUpdateVariationIds(): Promise<void> {
  if (!hasRun) {
    hasRun = true;
    await updateAllVariationsIds();
  }
}

/**
 * Retrieves the FeatureFlag value for the specified name.
 *
 * Will call check for updating variation ID if it is the first time called for the page.
 * Otherwise will return the value saved in session storage cache from the initial call on page load.
 *
 * Returns the default value if FeatBit fails to respond for any reason
 * @param key - The key of the feature flag to retrieve.
 * @param defaultValue - The default value to use in case the request to FeatBit errors.
 *
 * @example
 * ```jsx
 *  useEffect(() => {
 *   const getFlag = async () => {
 *      const flag = await getABFeatureFlagAndUpdateVariationId(
 *       "abvariation-2-02-SearchTest2",
 *      "a",
 *   );
 *      setFlag(flag);
 *   };
 *    getFlag();
 *  }, []);
 * ```
 */
export async function getABFeatureFlagAndUpdateVariationId(
  key: string,
  defaultValue: string,
): Promise<string> {
  if (!hasRun) {
    await getAndUpdateVariationIds();
    hasRun = true;
  }
  const value = sessionStorage.getItem(key);
  return value ?? defaultValue;
}

async function getActiveAbFeatBitFlags(
  sessionId: string,
  emailAddress: string,
): Promise<FeatureFlag[]> {
  const result = await getAllABVariationFlags(
    {
      customProps: [{ key: "Page", value: window.location.pathname }],
    },
    { sessionId, emailAddress },
  );
  if (result.isError) {
    // If there is an error getting the FeatBit flags, the variation Id should reflect that no tests are active for the user
    return [];
  }
  return result.value;
}

function buildVariationIdFromActiveABTests(
  activeVariationTests: DecodedTestInformation[],
): string {
  const enrolledTests: string[] = [];
  activeVariationTests.forEach((variation) => {
    enrolledTests.push(variation.fullVariationId);
  });
  return enrolledTests.join(",");
}

function getDecodedTestInformation(
  flag: FeatureFlag,
): DecodedTestInformation | undefined {
  const split = flag.name.split("-");
  const departmentId = split[1];
  const testId = split[2];
  const exclusive = split[4];
  const variant = flag.stringValue;
  let fullVariationId = "";

  if (!departmentId || !testId) return;

  const fullTestId = `${split[1]}${split[2]}`;
  fullVariationId += `${departmentId}${testId}:${variant}`;

  if (exclusive === "exclusive") {
    fullVariationId += ":exclusive";
  }
  return {
    fullTestId,
    fullVariationId,
    exclusive: exclusive === "exclusive",
    kind: flag.kind,
    variant,
  };
}

function getSavedCookieTestInformation(
  variationId?: string,
): DecodedTestInformation[] {
  if (!variationId) return [];
  const tests = variationId.split(",");
  const decodedTests: DecodedTestInformation[] = [];
  tests.forEach((test) => {
    const parts = test.split(":");
    const fullTestId = parts[0];
    const value = parts[1];
    const exclusive = parts[2];
    if (fullTestId && value) {
      decodedTests.push({
        fullTestId,
        exclusive: exclusive === "exclusive",
        fullVariationId: test,
        variant: value,
      });
    }
  });
  return decodedTests;
}

async function updateAllVariationsIds(): Promise<void> {
  const { user } = Store.getState();
  const allActiveFeatBitTests = await getActiveAbFeatBitFlags(
    user.sessionId ?? "",
    user.emailAddress ?? "",
  );
  // Future PBI 1380153 will need to account for CloudFlare cookies here
  const currentVariationIdFromCookie = getCurrentVariationIdFromCookie();

  const testsCurrentlySavedInCookie = getSavedCookieTestInformation(
    currentVariationIdFromCookie,
  );
  const allActiveFeatBitTestsInformation: DecodedTestInformation[] = [];
  allActiveFeatBitTests.forEach((abTest) => {
    const decoded = getDecodedTestInformation(abTest);
    if (decoded) {
      allActiveFeatBitTestsInformation.push(decoded);
    }
  });

  const expiredTestsInfo = getExpiredTestsFromCookie(
    testsCurrentlySavedInCookie,
    allActiveFeatBitTestsInformation,
  );
  if (expiredTestsInfo) {
    updateStorageForExpiredTests(
      expiredTestsInfo.tests,
      expiredTestsInfo.isExclusive,
    );
  }

  const enrolledFeatBitTests: DecodedTestInformation[] = [];
  // Exclusive tests always have priority when enrolling a user in a new test
  const exclusiveFeatBitTest = getExclusiveTestIfEligible(
    testsCurrentlySavedInCookie,
    allActiveFeatBitTestsInformation,
  );

  if (exclusiveFeatBitTest) {
    updateStorageForExclusiveTest(exclusiveFeatBitTest);
  }

  // Users in an exclusive test can ONLY be in an exclusive test
  if (exclusiveFeatBitTest) {
    enrolledFeatBitTests.push(exclusiveFeatBitTest);
  } else {
    const parsedActiveABFeatBitTests = parseActiveFeatBitTests(
      testsCurrentlySavedInCookie,
      allActiveFeatBitTestsInformation,
    );
    updateStorageForNewTests(parsedActiveABFeatBitTests.newTests);
    enrolledFeatBitTests.push(...parsedActiveABFeatBitTests.allEnrolledTests);
  }

  // CloudFlare tests will be added here for variation Id construction
  const sortedCombinedTests = [...enrolledFeatBitTests].sort((a, b) =>
    a.fullTestId.localeCompare(b.fullTestId),
  );
  const constructed = buildVariationIdFromActiveABTests(sortedCombinedTests);

  if (constructed !== currentVariationIdFromCookie) {
    handleChangedVariationIdCookie(constructed);
  }
}
